import { Check, SealPercent, WarningCircle } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import GenericButton from "components/common/buttons/GenericButton";
import InputField from "components/common/forms/InputField";
import { useLocale } from "lib/hooks/useLocale";
import { ErrorType } from "lib/types/address";
import React, { ChangeEvent, useState } from "react";
import PartnerOffersStrip from "./PartnerOffersStrip";
import { classNames } from "lib/utils/helpers";
import { DiscountConfigType, PartnerOffersConfig } from "lib/types/merchantConfigTypes";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { hexToRgba } from "@shopflo/ui/utils";
import MultiGiftCard from "../cards/MultiGiftCard";
import GiftCard from "../cards/GiftCard";
import Gyftr from "../cards/Gyftr";
interface CouponInputFieldProps {
  handleDiscountApply: (
    code: string,
    isManual?: boolean,
    overrideRemoval?: boolean,
    codeTitle?: string | undefined,
  ) => Promise<boolean>;
  showCoupons?: boolean | null;
  couponApplyError: ErrorType;
  renderCouponSummaryStrip: () => JSX.Element;
  setCouponApplyError: (value: React.SetStateAction<ErrorType>) => void;
  customisations?: DiscountConfigType;
  onOpenPartnerOffers?: () => void;
  partnerOffersConfig?: PartnerOffersConfig;
}

const CouponInputField: React.FC<CouponInputFieldProps> = ({
  handleDiscountApply,
  couponApplyError,
  renderCouponSummaryStrip,
  setCouponApplyError,
  showCoupons,
  customisations,
  onOpenPartnerOffers,
  partnerOffersConfig,
}) => {
  const [manualCoupon, setManualCoupon] = useState<string>("");
  const { t } = useLocale();

  return (
    <div
      className="flex flex-col rounded-xl overflow-hidden border"
      style={{
        ...getCardBorderStyles({
          borderSides: customisations?.cardBorderSides,
          borderWidth: customisations?.cardBorderWidth,
          borderColor: customisations?.input.borderColor,
        }),
        borderRadius: customisations?.cardRadius ?? 12,
      }}>
      {showCoupons && (
        <div
          className="border-box relative flex w-full px-0 flex-col"
          style={
            {
              "--input-bg": customisations?.input?.backgroundColor,
              "--input-text": customisations?.input?.primaryColor,
              "--input-placeholder-color": hexToRgba(customisations?.input?.primaryColor ?? "", 0.6),
            } as React.CSSProperties
          }>
          <Icon
            IconComponent={SealPercent}
            size="md"
            iconProps={{ weight: "fill", color: customisations?.input?.primaryColor }}
            className="absolute top-[8px] left-2 z-10 h-4 w-4 text-coal-light"></Icon>
          <InputField
            type="text"
            placeholder={t("enter_coupon_code")}
            name="coupon-code"
            id="coupon-code"
            maxLength={50}
            autoComplete="off"
            autoFocus={false}
            value={manualCoupon}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setCouponApplyError({ status: false, message: "" });
              setManualCoupon(e.target.value.trim());
            }}
            onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                const success = await handleDiscountApply(manualCoupon, true);
                if (success) {
                  setManualCoupon("");
                }
              }
            }}
            height="h-12"
            customClass={classNames(
              "!bg-[var(--input-bg)] !text-[var(--input-text)] !placeholder-[var(--input-placeholder-color)]",
              "text-text-sm-regular w-full !pl-[2.4rem] !rounded-none border-none",
              "!outline-none !ring-0 !shadow-none !border-none focus:!outline-none focus:!ring-0 focus:!shadow-none focus:!border-none",
            )}
            showLabel={false}
            tabIndex={-1}
          />
          {couponApplyError?.status && couponApplyError.message && (
            <div className="mt-1 flex items-center gap-1.5 pl-3 py-1">
              <WarningCircle className="-mt-[1px] h-4 w-4 shrink-0 text-ouch" />
              <label className="bg-transparent text-text-xs-regular text-ouch transition-all peer-focus:hidden">
                {couponApplyError?.message}
              </label>
            </div>
          )}
          {manualCoupon?.length > 0 && (
            <GenericButton
              buttonText=""
              height="h-9"
              width=""
              customClass="flex items-center justify-center rounded-lg w-9 rounded-2xl absolute right-1 top-1.5"
              style={{
                backgroundColor: customisations?.button.backgroundColor,
              }}
              iconComponent={
                <Check
                  className="h-5 w-5 text-white"
                  style={{
                    color: customisations?.button?.textColor,
                  }}
                />
              }
              onClick={async () => {
                const success = await handleDiscountApply(manualCoupon, true);
                if (success) {
                  setManualCoupon("");
                }
              }}
            />
          )}
        </div>
      )}
      {renderCouponSummaryStrip()}
      <PartnerOffersStrip
        withFooterContainer
        customisations={customisations}
        onOpenPartnerOffers={onOpenPartnerOffers}
        partnerOffersConfig={partnerOffersConfig}
      />
      <div className="bg-green-dark/5 text-green-dark">
        <GiftCard />
        <Gyftr />
        <MultiGiftCard />
      </div>
    </div>
  );
};

export default CouponInputField;
